// Centralized wrapper for marking references as opened in sessionStorage.
// Keys are generated by hashing a stable JSON.stringify of the whole reference
// object so keys are unique per-reference and deterministic.

const djb2hash = str => {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

// Stable stringify: sort object keys so the same content yields same string
const stableStringify = v => {
  const seen = new WeakSet();
  const sorter = (a, b) => (a < b ? -1 : a > b ? 1 : 0);

  const stringify = val => {
    if (val === null || val === undefined) return JSON.stringify(val);
    if (typeof val !== 'object') return JSON.stringify(val);
    if (seen.has(val)) return '"[Circular]"';
    seen.add(val);
    if (Array.isArray(val)) {
      return '[' + val.map(stringify).join(',') + ']';
    }
    const keys = Object.keys(val).sort(sorter);
    return '{' + keys.map(k => JSON.stringify(k) + ':' + stringify(val[k])).join(',') + '}';
  };

  return stringify(v);
};

const makeKeyFromString = s => `ref_opened:${djb2hash(s)}`;

const ReferenceReadStore = {
  // Accepts either a reference object or a string. If object, uses stable JSON
  keyFor(ref) {
    try {
      if (!ref && ref !== 0) return makeKeyFromString('');
      if (typeof ref === 'string') return makeKeyFromString(ref);
      // object -> stable stringify
      const s = stableStringify(ref);
      return makeKeyFromString(s);
    } catch (e) {
      try { return makeKeyFromString(String(ref)); } catch (ee) { return makeKeyFromString(''); }
    }
  },

  // Returns parsed value stored or null
  get(ref) {
    try {
      const key = this.keyFor(ref);
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      try { return JSON.parse(raw); } catch (e) { return { value: raw }; }
    } catch (e) { return null; }
  },

  isOpened(ref) {
    try {
      const key = this.keyFor(ref);
      return !!sessionStorage.getItem(key);
    } catch (e) {
      return false;
    }
  },

  // markOpened accepts either a reference object (preferred) or a string id/path.
  // We store a small JSON object with name and openedAt timestamp.
  markOpened(ref, name) {
    try {
      const key = this.keyFor(ref);
      const value = {
        openedAt: Date.now(),
        name: (ref && typeof ref === 'object' && ref.name) ? ref.name : (typeof name === 'string' ? name : '')
      };
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch (e) {
      return false;
    }
  },

  clearOpened(ref) {
    try {
      const key = this.keyFor(ref);
      sessionStorage.removeItem(key);
      return true;
    } catch (e) { return false; }
  }
};

export default ReferenceReadStore;
